import { Address } from 'viem';
import AssetImage from '../../elements/AssetImage';
import AssetCard from './AssetCard';
import { useMemo, useState } from 'react';
import { Asset, PREMIUM_DURATION, getChainConstant } from 'nftx.js';
import { useNetwork } from '@nftx/react';
import AuctionAssetCardContainer from './AuctionAssetCardContainer';
import { useModalContext } from '@/presentation/context/modal';
import useCollection from '@/application/read/collections/useCollection';

interface AssetContainerProps extends Partial<Asset> {
  assetAddress: Address;
  tokenId: `${number}`;
  imageUrl: string;
  onClick: () => void;
  price?: bigint;
  disabled?: boolean;
  selected?: boolean;
  dateAdded?: number;
  shadow?: boolean;
  vaultId?: string;
  is1155: boolean;
  selectedQuantity: number;
  onQuantityChange: (quantity: number) => void;
}

const AssetCardContainer = ({
  assetAddress,
  tokenId,
  quantity = 1n,
  onClick,
  price,
  dateAdded,
  disabled,
  selected,
  imageUrl,
  shadow,
  vaultId,
  rarity,
  rarityRank,
  is1155,
  onQuantityChange,
  selectedQuantity,
}: AssetContainerProps) => {
  const network = useNetwork();
  const { showAssetDetailModal } = useModalContext();
  const { data: collection } = useCollection(assetAddress);
  const [auctionStart] = useState(() => {
    if (!dateAdded) {
      return 0;
    }
    const now = Math.floor(Date.now() / 1000);
    if (dateAdded < now - getChainConstant(PREMIUM_DURATION, network)) {
      return 0;
    }
    return dateAdded;
  });
  const auctionEnd = useMemo(() => {
    if (!auctionStart) {
      return 0;
    }
    return auctionStart + getChainConstant(PREMIUM_DURATION, network);
  }, [auctionStart, network]);

  const isAuction = auctionEnd > auctionStart;

  const rarityPercentage =
    rarityRank && collection?.tokenCount
      ? rarityRank / collection.tokenCount
      : 1;

  const props = {
    shadow,
    media: (
      <AssetImage
        assetAddress={assetAddress}
        tokenId={tokenId}
        imageUrl={imageUrl}
        className="rounded-md overflow-hidden"
      />
    ),
    tokenId,
    onClick,
    rarity,
    rarityRank,
    rarityPercentage,
    start: auctionStart,
    expires: auctionEnd,
    isAuction,
    disabled,
    selected,
    quantity,
    is1155,
    selectedQuantity,
    onQuantityChange,
    onDetailsClick: () =>
      showAssetDetailModal({
        tokenId,
        assetAddress,
        quantity,
        collectionSlug: collection?.slug,
      }),
  };

  if (isAuction) {
    return (
      <AuctionAssetCardContainer
        dateAdded={dateAdded}
        vaultId={vaultId as string}
        {...props}
      />
    );
  }

  return <AssetCard {...props} price={price} />;
};

export default AssetCardContainer;
